import { t } from '@lingui/macro';

import CaretDown from '@decisiv/iconix/lib/components/CaretDown';
import Plus from '@decisiv/iconix/lib/components/Plus';
import Refresh from '@decisiv/iconix/lib/components/Refresh';
import Upload from '@decisiv/iconix/lib/components/Upload';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Menu from '@decisiv/ui-components/lib/components/Menu';
import Popover from '@decisiv/ui-components/lib/components/Popover';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import useMenuPopover from 'pages/Management/hooks/useMenuPopover';

import { MenuDivider } from './styles';

/**
 * Menu copy for the PD-5874 Fleet asset management Figma. Callers pass it
 * only with FEATURE_FLAGS.PD_5874_FLEET_ASSET_MANAGEMENT on; without it the
 * menu keeps the legacy labels.
 */
export interface AddAssetMenuCopy {
  manual: string;
  bulk: string;
  reset: string;
  /** Shown under the reset item. */
  resetHelpText?: string;
}

/**
 * The Figma copy for a menu whose reset acts on one depot or on the whole
 * account. Build it at render time so the strings are translated.
 */
export const fleetAssetManagementMenuCopy = (
  scope: 'depot' | 'account',
): AddAssetMenuCopy => ({
  manual: t`Add Assets by VIN`,
  bulk: t`Add Assets from File`,
  reset: t`Replace Asset List`,
  resetHelpText:
    scope === 'depot'
      ? t`Upload a CSV file with the complete list of assets for this depot. The file becomes the depot's asset inventory: assets not in it are released.`
      : t`Upload a CSV file with the complete list of assets for this account. The file becomes the account's asset inventory: assets not in it are released.`,
});

interface AddAssetMenuProps {
  disabled?: boolean;
  kind?: 'primary' | 'secondary';
  onSelectManual?: () => void;
  onSelectBulk?: () => void;
  /**
   * Switches the control to the "Manage Assets" variant: relabelled button,
   * plus a second menu below a divider. Omitted by the Accounts index header
   * button, which spans every account and so has none for a reset to target.
   */
  onSelectReset?: () => void;
  copy?: AddAssetMenuCopy;
}

export default function AddAssetMenu({
  disabled,
  kind = 'secondary',
  onSelectManual,
  onSelectBulk,
  onSelectReset,
  copy,
}: AddAssetMenuProps) {
  const { triggerProps, popoverProps, closeOnSelect } = useMenuPopover();

  const items = closeOnSelect([
    {
      id: 'manual',
      text: copy?.manual ?? t`Add Asset`,
      icon: Plus,
      disabled: !onSelectManual,
      onSelect: onSelectManual,
    },
    {
      id: 'bulk',
      text: copy?.bulk ?? t`Bulk Add Assets`,
      icon: Upload,
      disabled: !onSelectBulk,
      onSelect: onSelectBulk,
    },
  ]);

  const resetItems = closeOnSelect([
    {
      id: 'reset',
      text: copy?.reset ?? t`Reset Depots Assets`,
      icon: Refresh,
      onSelect: onSelectReset,
    },
  ]);

  return (
    <>
      <Button
        text={onSelectReset ? t`Manage Assets` : t`Add Asset`}
        icon={CaretDown}
        iconPosition="right"
        size="small"
        kind={kind}
        disabled={disabled}
        {...triggerProps}
      />
      <Popover {...popoverProps} zIndex={200}>
        <Flex flexDirection="column">
          <Menu items={items} />
          {onSelectReset && (
            <>
              <MenuDivider />
              <Menu items={resetItems} />
              {copy?.resetHelpText && (
                <P
                  size="small"
                  shade={1}
                  paddingX={1}
                  paddingBottom={0.5}
                  style={{ maxWidth: 280 }}
                >
                  {copy.resetHelpText}
                </P>
              )}
            </>
          )}
        </Flex>
      </Popover>
    </>
  );
}
